import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contentResource } from './api';
import { getCollection } from '@/config/collections';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { useLocale } from '@/lib/i18n/useLocale';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { resolveI18n, isArComplete } from '@/lib/i18n/content';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { ContentStatusPill } from '@/lib/ui/StatusPill';
import { Tag } from '@/lib/ui/Tag';
import { Button, PermissionButton } from '@/lib/ui/Button';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { ReferenceSelectField } from '@/lib/forms/widgets/ReferenceSelectField';
import { vocabSelectOptions } from '@/config/vocab';
import { useToast } from '@/lib/ui';
import { formatRelative } from '@/lib/format';
import type { BulkAction, ContentItem } from '@/lib/api/types';

/** Config-driven list for any of the 7 collections (spec §9.3). */
export function ContentListPage() {
  const { t } = useTranslation();
  const { collection = '' } = useParams();
  const cfg = getCollection(collection);
  const navigate = useNavigate();

  const STATUS_OPTIONS = [
    { value: '', label: t('content.statusFilter.all') },
    { value: 'draft', label: t('content.statusFilter.draft') },
    { value: 'scheduled', label: t('content.statusFilter.scheduled') },
    { value: 'published', label: t('content.statusFilter.published') },
    { value: 'archived', label: t('content.statusFilter.archived') },
  ];
  const { locale } = useLocale();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const res = contentResource(collection);

  const list = useListState({ status: '' });
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounced(rawSearch, 300);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { allowed: canWrite } = useGate('content:write');
  const { allowed: canPublish } = useGate('content:publish');
  const { allowed: canDelete } = useGate('content:delete');

  const query = useQuery({
    queryKey: keys.content.list(collection, { ...list.params, q: debounced }),
    queryFn: () => res.list({ ...list.params, q: debounced || undefined }),
    placeholderData: (prev) => prev,
    enabled: !!cfg,
  });

  const bulk = useMutation({
    mutationFn: (action: BulkAction) => res.bulk(action, [...selected]),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.content.all(collection) });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      setSelected(new Set());
      toast.success(
        t('content.bulkResult', { action: r.action, succeeded: r.succeeded, failed: r.failed }),
        r.failed > 0 ? t('content.bulkPartialFail') : undefined,
      );
    },
    onError,
  });

  if (!cfg) return <p className="text-small text-ink-mute">{t('content.unknownCollection')}</p>;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columns: Column<ContentItem>[] = [
    {
      id: 'title',
      header: t('common.title'),
      cell: (r) => <span className="font-medium text-ink-deep">{resolveI18n(r.title, locale)}</span>,
    },
    { id: 'status', header: t('common.status'), cell: (r) => <ContentStatusPill status={r.status} /> },
    {
      id: 'ident',
      header: cfg.identType === 'slug' ? t('common.slug') : t('common.key'),
      cell: (r) => <MonoValue className="text-ink-mid">{r.slug || r.key}</MonoValue>,
    },
    {
      id: 'translation',
      header: t('content.arShort'),
      cell: (r) =>
        isArComplete(r.doc) ? (
          <Tag variant="teal">{t('content.arShort')} ✓</Tag>
        ) : (
          <Tag variant="amber">{t('content.arShort')} ·</Tag>
        ),
    },
    {
      id: 'sort',
      header: t('content.order'),
      align: 'end',
      cell: (r) => <MonoValue>{r.sort_order}</MonoValue>,
    },
    {
      id: 'updated',
      header: t('common.updated'),
      align: 'end',
      cell: (r) => <span className="text-ink-mute">{formatRelative(r.updated_at)}</span>,
    },
  ];

  const collectionLabel = t(cfg.label, { defaultValue: cfg.label });

  return (
    <div>
      <PageHeader
        eyebrow={collectionLabel.toUpperCase()}
        title={collectionLabel}
        count={query.data?.meta?.pagination.total}
        actions={
          cfg.editOnly ? undefined : (
            <PermissionButton
              allowed={canWrite}
              reason={t('content.newItemPermission')}
              onClick={() => navigate(`/content/${collection}/new`)}
            >
              {t('content.newItem')}
            </PermissionButton>
          )
        }
      />

      {/* Filters */}
      <div className="mb-md flex flex-wrap items-end gap-sm">
        <div className="w-[180px]">
          <Select
            label={t('common.status')}
            value={(list.filters.status as string) || ''}
            onValueChange={(v) => list.setFilter('status', v)}
            options={STATUS_OPTIONS}
          />
        </div>
        {cfg.facets.map((f) => {
          if (f.type === 'boolean') {
            return (
              <div key={f.key} className="pb-[6px]">
                <Switch
                  label={t(f.label, { defaultValue: f.label })}
                  checked={list.filters[f.key] === true}
                  onCheckedChange={(v) => list.setFilter(f.key, v || undefined)}
                />
              </div>
            );
          }
          if (f.type === 'enum') {
            return (
              <div key={f.key} className="w-[180px]">
                <Select
                  label={t(f.label, { defaultValue: f.label })}
                  value={(list.filters[f.key] as string) || ''}
                  onValueChange={(v) => list.setFilter(f.key, v || undefined)}
                  options={[
                    { value: '', label: t('content.filterAll') },
                    ...vocabSelectOptions(f.options ?? [], (k) => t(k)),
                  ]}
                />
              </div>
            );
          }
          if (f.type === 'ref-slug') {
            return (
              <div key={f.key} className="w-[200px]">
                <ReferenceSelectField
                  label={t(f.label, { defaultValue: f.label })}
                  collection={f.refCollection ?? 'industries'}
                  value={(list.filters[f.key] as string) || ''}
                  onChange={(slug) => list.setFilter(f.key, slug || undefined)}
                />
              </div>
            );
          }
          return (
            <div key={f.key} className="w-[160px]">
              <TextInput
                label={t(f.label, { defaultValue: f.label })}
                value={(list.filters[f.key] as string) || ''}
                onChange={(e) => list.setFilter(f.key, e.target.value || undefined)}
              />
            </div>
          );
        })}
        <div className="pb-[6px]">
          <Switch
            label={t('content.missingAr')}
            checked={list.filters.missing_ar === true}
            onCheckedChange={(v) => list.setFilter('missing_ar', v || undefined)}
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <TextInput label={t('common.search')} value={rawSearch} onChange={(e) => setRawSearch(e.target.value)} />
        </div>
      </div>

      {/* Bulk bar (spec §9.3 / gap 16) */}
      {selected.size > 0 && (
        <div className="mb-sm flex items-center gap-sm rounded-md border-[0.5px] border-accent-blue bg-canvas-deep-sand px-md py-sm">
          <span className="text-small text-ink-deep">
            <MonoValue>{selected.size}</MonoValue> {t('content.selected')}
          </span>
          <PermissionButton allowed={canPublish} reason={t('content.publishPermission')} size="sm" loading={bulk.isPending} onClick={() => bulk.mutate('publish')}>
            {t('common.publish')}
          </PermissionButton>
          <PermissionButton allowed={canPublish} reason={t('content.publishPermission')} variant="ghost" size="sm" onClick={() => bulk.mutate('unpublish')}>
            {t('common.unpublish')}
          </PermissionButton>
          {!cfg.editOnly && (
            <PermissionButton allowed={canDelete} reason={t('content.deletePermission')} variant="danger" size="sm" onClick={() => bulk.mutate('delete')}>
              {t('common.delete')}
            </PermissionButton>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {t('common.clear')}
          </Button>
        </div>
      )}

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <DataTable
              columns={columns}
              rows={d.data}
              rowKey={(r) => r.id}
              onRowClick={(r) => navigate(`/content/${collection}/${r.id}`)}
              selectable
              selectedIds={selected}
              onToggleSelect={toggle}
              onToggleSelectAll={(checked) =>
                setSelected(checked ? new Set(d.data.map((r) => r.id)) : new Set())
              }
            />
            {d.meta?.pagination && (
              <Pagination meta={d.meta.pagination} onPageChange={list.setPage} onPerPageChange={list.setPerPage} />
            )}
          </>
        )}
      </DataView>
    </div>
  );
}
